import { Form, Formik } from 'formik';
import { useEffect, useRef, useState } from 'react';
import { v4 as uuidv4 } from 'uuid';
import InputField from 'src/components/common/formFields/inputField/InputField';
import EmojiPicker from 'emoji-picker-react';
import Button from 'src/components/button/Button';
import { ActionIcon } from 'src/components/common';
import { EmojiIcon, PlusIcon } from 'src/components/common/common.icons';
import { colors } from 'src/constants';
import { useGetIdentity, useUploadMedia } from 'src/hooks';
import { useAppDispatch, useAppSelector } from 'src/store/hooks';
import {
  TEditIdeaMessageRequest,
  TGetChatFilePresignedUrlActionRequest,
  TSendIdeaGroupMessageRequest,
  TSendDirectMessageRequest,
  TEditDirectMessageRequest
} from 'src/services/apiEndpoint.types.ts';
import {
  clearChatFiles,
  editIdeaMessage,
  getChatFilePresignedUrl,
  sendIdeaGroupMessage,
  editDirectMessage,
  sendDirectMessage
} from 'src/store/chat/chat.slice';
import FileCard from 'src/components/chat/fileCard/FileCard';
import { openPopup } from 'src/store/common/common.slice';

import './chatMessagesFooter.scss';
import { TChatMessagesFooterProps, TChatMessageInitialValues } from './chatMessagesFooter.types';
import { LuLink } from 'react-icons/lu';

const ChatMessagesFooter = ({
  placeholder,
  groupId,
  editMessageSettings,
  updateMessageSettings,
  groupMessageContext,
  lastMessageRef = null
}: TChatMessagesFooterProps) => {
  // Hooks
  const dispatch = useAppDispatch();
  const { getIdentities } = useGetIdentity();
  const { filesToUpload } = useAppSelector((store) => store.chat);
  const { handleUploadMedia } = useUploadMedia();

  // State
  const [uploadedFiles, setUploadedFiles] = useState(filesToUpload)
  const [isEmojiPickerOpen, setIsEmojiPickerOpen] = useState<boolean>(false);
  const [messageId, setMessageId] = useState<string>(uuidv4());
  const uploadFileRef = useRef<HTMLInputElement>(null);

  // Constants
  const { authenticatedId, loggedInUserType } = getIdentities();
  const isEditMessageMode: boolean = editMessageSettings?.editMode;

  const initialValues: TChatMessageInitialValues = isEditMessageMode
    ? { message: editMessageSettings.message }
    : { message: '' };

  const turnEditModeOff = () => {
    updateMessageSettings({ editMode: false, messageId: '', message: '', files: [] });
  };

  const openImageFileManager = async () => {
    if (uploadFileRef.current) {
      uploadFileRef.current.click();
    }
  };

  const onMediaLoad = (
    data: string | ArrayBuffer | null,
    fileType: string,
    mediaType: string,
    name: string,
    size: number
  ) => {
    const fileId: string = uuidv4();

    const payload: TGetChatFilePresignedUrlActionRequest = {
      conversationId: groupId,
      messageId,
      id: fileId,
      fileName: name,
      fileData: data
    };
    dispatch(getChatFilePresignedUrl(payload));
  };

  const openEmptyMessagePopup = () => {
    dispatch(
      openPopup({
        popupMessage: 'You message is empty, Please type something before sending',
        popupType: 'error'
      })
    );
  };

  const handleSubmit = (
    values: TChatMessageInitialValues,
    { resetForm }: { resetForm: () => void }
  ) => {
    const { message } = values;

    if (groupId) {
      if (isEditMessageMode) {
        const payload: TEditDirectMessageRequest = {
          eventName: 'edit-conversation-message',
          messageId: editMessageSettings?.messageId,
          message
        };
        if (message?.trim()) {
          dispatch(editDirectMessage(payload));
          turnEditModeOff();
          resetForm();
        } else {
          openEmptyMessagePopup();
        }
      } else if (!isEditMessageMode) {
        const payload: TSendDirectMessageRequest = {
          userId: authenticatedId,
          userType: loggedInUserType,
          conversationId: groupId,
          eventName: 'conversation',

          message,
          messageId,
          files: filesToUpload
        };

        if (filesToUpload?.length > 0 || message?.trim()) {
          dispatch(sendDirectMessage(payload));
          resetForm();
          setMessageId(uuidv4());
          dispatch(clearChatFiles());
        } else {
          openEmptyMessagePopup();
        }
      }
      lastMessageRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
    }
  };
  useEffect(() => {
    setUploadedFiles(filesToUpload)
  }, [filesToUpload])
  useEffect(() => {
    if (editMessageSettings?.files?.length > 0 && editMessageSettings?.editMode) {
      setUploadedFiles(editMessageSettings?.files)
    }
  }, [editMessageSettings?.files])

  return (
    <div className="dm-chat-messages-footer">
      <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
        {({ setFieldValue, values }) => {
          return (
            <Form className="dm-chat-messages-footer__form">
              <div className="dm-chat-messages-footer__form__row">
                <InputField id="message" placeholder={placeholder} value={values?.message} />
                <div>

                  <div className="dm-chat-messages-footer__form__row__actions">
                    <div className="dm-chat-messages-footer__form__row__actions">
                      <input
                        type="file"
                        hidden
                        data-testid="inputUploadImage"
                        ref={uploadFileRef}
                        accept=".bmp,.jpg,.jpeg,.png,.pdf,.mp4,.webm"
                        className="select-post-image"
                        multiple
                        onChange={(e) => {
                          handleUploadMedia({ e, onLoad: onMediaLoad, mediaType: 'program' });
                        }}
                      />
                      {!editMessageSettings?.editMode && (
                        <div className='emojiLink_icons' onClick={openImageFileManager}>
                          <LuLink color={colors?.neutral?.[700]} />
                        </div>
                      )}
                      <div className='emojiLink_icons' onClick={() => setIsEmojiPickerOpen((prev) => !prev)}>
                        <EmojiIcon />
                      </div>
                    </div>{' '}
                    <EmojiPicker
                      open={isEmojiPickerOpen}
                      onEmojiClick={(e) => {
                        setFieldValue('message', values.message + e.emoji);
                        setIsEmojiPickerOpen(false);
                      }}
                    />
                    {editMessageSettings.editMode && (
                      <Button
                        size="small"
                        variant="secondary"
                        type="button"
                        className="ihg-chat-messages-footer__form__row__actions__cancel"
                        onClick={turnEditModeOff}
                      >
                        Cancel
                      </Button>
                    )}
                    <Button size="small" variant="primary" type="submit">
                      Send
                    </Button>
                  </div>
                </div>
              </div>

              {uploadedFiles?.length > 0 && !editMessageSettings?.editMode && (
                <div className="dm-chat-messages-footer__form__row__files">
                  {uploadedFiles.map(({ id, fileName }) => (
                    <FileCard
                      key={id}
                      id={id}
                      fileName={fileName}
                      showDownloadButton={false}
                      showDeleteButton={true}
                    />
                  ))}
                </div>
              )}
            </Form>
          );
        }}
      </Formik>
    </div>
  );
};

export default ChatMessagesFooter;
